import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';

import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import type { MessageAudienceStudent } from '@/types/messaging';

interface RecipientPickerProps {
  students: MessageAudienceStudent[];
  selected: Set<string>;
  onToggle: (uuid: string) => void;
  /** Ticks or clears every student the current search is showing. */
  onSelectAll: (uuids: string[], next: boolean) => void;
  disabled?: boolean;
}

/**
 * Choosing which children's families hear about this.
 *
 * Only the reachable can be ticked. A student with no number recorded is still
 * listed — leaving them out would have the office hunting for a child who is on
 * the roll and cannot be found here — but the row says why it cannot be chosen
 * rather than accepting a tick that would silently do nothing.
 *
 * Select-all follows the search rather than the whole roll. "Select all" while
 * looking at a search for "Class 8" plainly means those, and a control that
 * quietly ticked two hundred instead would be found out one message too late.
 */
export function RecipientPicker({
  students,
  selected,
  onToggle,
  onSelectAll,
  disabled = false,
}: RecipientPickerProps) {
  const [search, setSearch] = useState('');

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return students;
    return students.filter((student) => student.name.toLowerCase().includes(needle));
  }, [students, search]);

  /** Only the reachable ones can be ticked, so only they count towards it. */
  const selectable = shown.filter((student) => student.isReachable).map((student) => student.uuid);
  const chosenHere = selectable.filter((uuid) => selected.has(uuid)).length;
  const allChosen = selectable.length > 0 && chosenHere === selectable.length;

  return (
    <div className="flex flex-col gap-2">
      <Input
        label="Search students"
        hideLabel
        placeholder="Search by name"
        value={search}
        disabled={disabled}
        onChange={(event) => setSearch(event.target.value)}
        leftIcon={<Search className="h-4 w-4" />}
      />

      <div className="overflow-hidden rounded-card border border-line">
        <div className="flex items-center gap-2 border-b border-line bg-surface-2 px-3 py-2">
          <Checkbox
            label={
              search.trim()
                ? `Select the ${selectable.length} students this search shows`
                : `Select all ${selectable.length} students`
            }
            hideLabel
            checked={allChosen}
            indeterminate={chosenHere > 0 && !allChosen}
            disabled={disabled || selectable.length === 0}
            onChange={(event) => onSelectAll(selectable, event.target.checked)}
          />
          <span className="text-xs text-ink-2">
            {search.trim() ? `Select all ${selectable.length} shown` : 'Select all'}
          </span>
          <span className="ml-auto text-xs text-ink-3">{selected.size} chosen</span>
        </div>

        <ul className="max-h-56 overflow-y-auto divide-y divide-line">
          {shown.length === 0 && (
            <li className="px-3 py-6 text-center text-xs text-ink-3">
              No student on the roll matches that.
            </li>
          )}

          {shown.map((student) => {
            const isChosen = selected.has(student.uuid);

            return (
              <li key={student.uuid}>
                <label
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 transition-colors duration-150',
                    student.isReachable && !disabled
                      ? 'cursor-pointer hover:bg-surface-2'
                      : 'cursor-not-allowed',
                    isChosen && 'bg-accent-soft',
                  )}
                >
                  <Checkbox
                    label={
                      student.isReachable
                        ? `Send to ${student.name}`
                        : `${student.name} has no mobile number recorded`
                    }
                    hideLabel
                    checked={isChosen}
                    disabled={disabled || !student.isReachable}
                    onChange={() => onToggle(student.uuid)}
                  />

                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate text-sm',
                      student.isReachable ? 'text-ink' : 'text-ink-3',
                    )}
                  >
                    {student.name}
                  </span>

                  {!student.isReachable && (
                    <span className="shrink-0 text-2xs text-warn">No number</span>
                  )}
                </label>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
